// The cards' holographic foil, drawn by one shared WebGL context into a 2D
// canvas on each card face (the text stays as page text, above it).
//
// Per pixel, it works out the card's surface as the page draws it (its turn,
// its own camera in front of it) and reflects the view off it, as real foil:
// - diffraction: foil is a fine grating; light splits into a rainbow where the
//   half vector between the light and the eye lies in the grating's plane, its
//   colour set by the angle along the grating. Each small patch has its own
//   grating direction (a mosaic), so the rainbow breaks into shimmering pixels.
// - flakes: tiny specks, each tilted a little differently, flashing when its own
//   reflection meets the light, so they twinkle as the card turns.
// - chrome: the edge and the NTO mark mirror a soft studio light.
//
// Regions come from a mask drawn once per face (R panel, G mark, B body, A card).

/** the face in layout units (the card is 404 wide; --k scales them on the page) */
const U = { w: 404, h: 650 }
const MASK = 1.25 // mask pixels per unit

const VERT = `#version 300 es
in vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }`

const FRAG = `#version 300 es
precision highp float;
uniform sampler2D uMask;
uniform vec2 uRes;      // drawing buffer size (px)
uniform vec2 uSize;     // displayed size of the face (css px)
uniform mat3 uM;        // the face's turn (its normal is uM * z)
uniform vec3 uC;        // its centre, relative to its camera's axis (css px)
uniform float uPersp;   // the camera's distance
uniform float uMk;      // the mark's own fade (the top card becoming the modal)
uniform float uBack;
out vec4 o;

float h1(vec2 c) { return fract(sin(dot(c, vec2(127.1, 311.7))) * 43758.5453); }
vec2 h2(vec2 c) { return fract(sin(vec2(dot(c, vec2(127.1, 311.7)), dot(c, vec2(269.5, 183.3)))) * 43758.5453); }
vec3 hue(float t) { return clamp(abs(fract(t + vec3(0.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0) - 1.0, 0.0, 1.0); }

const vec3 L1 = normalize(vec3(-0.38, -0.5, 1.0)); // key light: up left, in front
const vec3 L2 = normalize(vec3(0.75, 0.15, 0.65)); // rim light: right

// diffraction from a grating along g (in the card's plane)
vec3 grating(vec3 H, vec3 N, vec3 g, float width, float shift, float off) {
    vec3 b = cross(N, g);
    // off: where across the card the band falls (the foil's grooves curve a little)
    float on = exp(-pow((dot(H, b) + off) / width, 2.0));
    float t = dot(H, g) * 2.6 + shift;
    // two orders of the spectrum, the second fainter
    vec3 c = hue(t) * smoothstep(0.0, 0.15, abs(dot(H, g))) + 0.35 * hue(t * 2.0 + 0.3);
    return c * on;
}

vec3 chrome(vec3 R) {
    float k1 = pow(max(dot(R, L1), 0.0), 5.0);
    float k2 = pow(max(dot(R, L2), 0.0), 12.0);
    float sky = smoothstep(-0.7, 0.7, -R.y);
    float band = smoothstep(0.08, 0.0, abs(R.y + 0.12)) * 0.25;
    float v = 0.16 + 0.3 * sky + 0.85 * k1 + 0.5 * k2 + band;
    return vec3(v) * vec3(0.96, 0.98, 1.04);
}

void main() {
    vec2 uv = vec2(gl_FragCoord.x / uRes.x, 1.0 - gl_FragCoord.y / uRes.y);
    vec4 m = texture(uMask, uv);
    if (m.a < 0.002) { o = vec4(0.0); return; }
    vec2 u = uv * vec2(${U.w}.0, ${U.h}.0); // layout units

    vec3 N = normalize(uM * vec3(0.0, 0.0, 1.0));
    vec3 T = normalize(uM * vec3(1.0, 0.0, 0.0));
    vec3 B = normalize(uM * vec3(0.0, 1.0, 0.0));
    vec3 P = uC + uM * vec3((uv - 0.5) * uSize, 0.0);
    vec3 V = normalize(P - vec3(0.0, 0.0, uPersp));
    if (dot(N, V) > 0.0) N = -N; // the side towards the camera
    vec3 R = reflect(V, N);
    vec3 H = normalize(L1 - V);

    // a mosaic of gratings: each patch its own direction
    float cs = mix(3.4, 2.6, m.g);
    vec2 cell = floor(u / cs);
    float a = h1(cell) * 3.14159;
    vec3 g = normalize(T * cos(a) + B * sin(a));
    vec3 mosaic = grating(H, N, g, 0.1, h1(cell + 7.0) * 0.25, 0.0);
    // one large diagonal grating under it: the big rainbow sweeping across
    // two diagonal streaks of light, prism stripes in them, moving as the card turns
    vec2 q = uv - 0.5;
    vec3 sweep = grating(H, N, normalize(T * 0.8 + B * 0.6), 0.07, dot(q, vec2(1.4, -1.0)) * 2.2, dot(q, vec2(0.6, -0.8)) * 0.45)
               + 0.7 * grating(H, N, normalize(T * 0.55 - B * 0.83), 0.05, dot(q, vec2(0.9, 1.2)) * 2.0 + 0.5, dot(q, vec2(0.83, 0.55)) * 0.4 + 0.08);

    // flakes: each tilted a little, flashing when its reflection meets a light
    vec2 fc = floor(u / 1.7);
    vec2 r2 = h2(fc) * 2.0 - 1.0;
    vec3 Nf = normalize(N + (T * r2.x + B * r2.y) * 0.32);
    vec3 Rf = reflect(V, Nf);
    float fl = pow(max(dot(Rf, L1), 0.0), 220.0) + 0.6 * pow(max(dot(Rf, L2), 0.0), 260.0);
    fl *= step(0.35, h1(fc + 3.1)); // not every speck
    vec3 flc = mix(vec3(1.0), hue(h1(fc + 11.0) + dot(Rf, T)), 0.65) * fl;

    float sheen = pow(max(dot(R, L1), 0.0), 30.0);

    vec3 body = vec3(0.035) + flc * 0.22 + mosaic * 0.04;
    vec3 panel = vec3(0.05, 0.055, 0.078) + mosaic * 0.3 + sweep * 1.1 + flc * 1.3 + sheen * 0.18 * vec3(0.8, 0.9, 1.0);
    vec3 ch = chrome(R);
    vec3 mark = ch * 0.92 + (mosaic * 0.35 + sweep * 0.3) * (0.4 + ch) + flc * 1.6;

    vec3 c = body;
    c = mix(c, panel, m.r);
    c = mix(c, mark, m.g * uMk);
    c = mix(ch * 0.9, c, m.b); // outside the body: the chrome edge
    o = vec4(c * m.a, m.a);
}`

function maskFront(): HTMLCanvasElement {
    const c = document.createElement("canvas")
    c.width = Math.round(U.w * MASK)
    c.height = Math.round(U.h * MASK)
    const x = c.getContext("2d")!
    x.scale(MASK, MASK)
    shape(x)
    x.globalCompositeOperation = "lighter"
    x.fillStyle = "#f00"
    round(x, 17, 17, U.w - 34, U.h - 17 - 208, 6)
    return c
}

function maskBack(img: HTMLImageElement | null): HTMLCanvasElement {
    const c = document.createElement("canvas")
    c.width = Math.round(U.w * MASK)
    c.height = Math.round(U.h * MASK)
    const x = c.getContext("2d")!
    x.scale(MASK, MASK)
    shape(x)
    if (img) {
        // the mark, green
        const t = document.createElement("canvas")
        t.width = c.width
        t.height = c.height
        const y = t.getContext("2d")!
        y.scale(MASK, MASK)
        y.drawImage(img, 28, 29, 64, U.h - 29 - 23)
        y.globalCompositeOperation = "source-in"
        y.fillStyle = "#0f0"
        y.fillRect(0, 0, U.w, U.h)
        x.setTransform(1, 0, 0, 1, 0, 0)
        x.globalCompositeOperation = "lighter"
        x.drawImage(t, 0, 0)
    }
    return c
}

// the card (alpha) and its body inside the 2-unit edge (blue)
function shape(x: CanvasRenderingContext2D) {
    x.fillStyle = "rgba(0,0,0,1)"
    round(x, 0, 0, U.w, U.h, 15.5)
    x.globalCompositeOperation = "source-over"
    x.fillStyle = "#00f"
    round(x, 2, 2, U.w - 4, U.h - 4, 10)
}

function round(x: CanvasRenderingContext2D, l: number, t: number, w: number, h: number, r: number) {
    x.beginPath()
    x.roundRect(l, t, w, h, r)
    x.fill()
}

export type FoilFace = {
    canvas: HTMLCanvasElement
    back: boolean
}

export type FoilView = {
    /** displayed size (css px) */
    w: number
    h: number
    /** the card's turn (deg) and over which axis */
    rx: number
    axis: "x" | "y"
    /** its centre relative to its camera's axis, and the camera's distance (css px) */
    cx: number
    cy: number
    z: number
    persp: number
    mark: number
}

export class Foil {
    private gl: WebGL2RenderingContext
    private prog: WebGLProgram
    private tex: { front: WebGLTexture; back: WebGLTexture }
    private loc: Record<string, WebGLUniformLocation | null> = {}
    private drawn = new WeakMap<HTMLCanvasElement, string>()
    private ctx = new WeakMap<HTMLCanvasElement, CanvasRenderingContext2D>()

    static create(onReady: () => void): Foil | null {
        try {
            const c = document.createElement("canvas")
            const gl = c.getContext("webgl2", { premultipliedAlpha: true, alpha: true, antialias: false, preserveDrawingBuffer: true })
            if (!gl) return null
            return new Foil(gl, onReady)
        } catch {
            return null
        }
    }

    private constructor(gl: WebGL2RenderingContext, onReady: () => void) {
        this.gl = gl
        const sh = (type: number, src: string) => {
            const s = gl.createShader(type)!
            gl.shaderSource(s, src)
            gl.compileShader(s)
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader")
            return s
        }
        const p = gl.createProgram()!
        gl.attachShader(p, sh(gl.VERTEX_SHADER, VERT))
        gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FRAG))
        gl.linkProgram(p)
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "link")
        this.prog = p
        gl.useProgram(p)
        const buf = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, buf)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
        const at = gl.getAttribLocation(p, "p")
        gl.enableVertexAttribArray(at)
        gl.vertexAttribPointer(at, 2, gl.FLOAT, false, 0, 0)
        for (const n of ["uMask", "uRes", "uSize", "uM", "uC", "uPersp", "uMk", "uBack"]) this.loc[n] = gl.getUniformLocation(p, n)
        gl.uniform1i(this.loc.uMask, 0)
        this.tex = { front: this.texture(maskFront()), back: this.texture(maskBack(null)) }
        const img = new Image()
        img.onload = () => {
            this.upload(this.tex.back, maskBack(img))
            this.drawn = new WeakMap()
            onReady()
        }
        img.src = "/about/nto-mark.svg"
    }

    private texture(src: HTMLCanvasElement) {
        const gl = this.gl
        const t = gl.createTexture()!
        this.upload(t, src)
        return t
    }
    private upload(t: WebGLTexture, src: HTMLCanvasElement) {
        const gl = this.gl
        gl.bindTexture(gl.TEXTURE_2D, t)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    }

    /** Is this face turned towards its camera? */
    static facing(v: FoilView, back: boolean) {
        const n = normal(v, back)
        // from the face's centre to the camera
        return n[0] * -v.cx + n[1] * -v.cy + n[2] * (v.persp - v.z) > 0
    }

    draw(face: FoilFace, v: FoilView) {
        const gl = this.gl
        const dpr = Math.min(devicePixelRatio || 1, 1.5)
        // (sharp enough, never huge)
        const s = Math.min(dpr, 1600 / Math.max(1, v.h))
        const w = Math.max(1, Math.round(v.w * s)),
            h = Math.max(1, Math.round(v.h * s))
        const key = [w, h, v.rx.toFixed(2), v.cx.toFixed(1), v.cy.toFixed(1), v.z.toFixed(1), v.persp.toFixed(0), v.mark.toFixed(3), v.axis].join()
        if (this.drawn.get(face.canvas) === key) return
        this.drawn.set(face.canvas, key)
        const cv = gl.canvas as HTMLCanvasElement
        if (cv.width < w || cv.height < h) {
            cv.width = Math.max(cv.width, w)
            cv.height = Math.max(cv.height, h)
        }
        gl.viewport(0, 0, w, h)
        gl.useProgram(this.prog)
        gl.activeTexture(gl.TEXTURE0)
        gl.bindTexture(gl.TEXTURE_2D, face.back ? this.tex.back : this.tex.front)
        gl.uniform2f(this.loc.uRes, w, h)
        gl.uniform2f(this.loc.uSize, v.w, v.h)
        gl.uniformMatrix3fv(this.loc.uM, false, matrix(v, face.back))
        gl.uniform3f(this.loc.uC, v.cx, v.cy, v.z)
        gl.uniform1f(this.loc.uPersp, v.persp)
        gl.uniform1f(this.loc.uMk, v.mark)
        gl.uniform1f(this.loc.uBack, face.back ? 1 : 0)
        gl.clearColor(0, 0, 0, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
        gl.drawArrays(gl.TRIANGLES, 0, 3)
        const c = face.canvas
        if (c.width !== w || c.height !== h) ((c.width = w), (c.height = h))
        let x = this.ctx.get(c)
        if (!x) this.ctx.set(c, (x = c.getContext("2d")!))
        x.clearRect(0, 0, w, h)
        x.drawImage(cv, 0, cv.height - h, w, h, 0, 0, w, h)
    }
}

// CSS rotations (y down, z towards the viewer), column-major for GLSL
function rotY(a: number) {
    const c = Math.cos(a),
        s = Math.sin(a)
    return [c, 0, -s, 0, 1, 0, s, 0, c]
}
function rotX(a: number) {
    const c = Math.cos(a),
        s = Math.sin(a)
    return [1, 0, 0, 0, c, s, 0, -s, c]
}
function mul(a: number[], b: number[]) {
    const r = new Array(9).fill(0)
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) r[j * 3 + i] += a[k * 3 + i] * b[j * 3 + k]
    return r
}
const D = Math.PI / 180
function matrix(v: FoilView, back: boolean) {
    // the card's turn, then (for the back) its half turn as the page draws it
    const turn = v.axis === "x" ? rotY(-v.rx * D) : rotX(v.rx * D)
    const flip = back ? (v.axis === "x" ? rotY(Math.PI) : rotX(Math.PI)) : [1, 0, 0, 0, 1, 0, 0, 0, 1]
    return new Float32Array(mul(turn, flip))
}
function normal(v: FoilView, back: boolean) {
    const m = matrix(v, back)
    return [m[6], m[7], m[8]]
}
